import { App } from 'App'

import { FC, useCallback, useContext, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { toast } from 'react-toastify'
import { VerificationPage } from 'ufinet-web-components'
import {
	AuthContext,
	authService,
	AuthStatus,
	IAuthUser,
	ITokenData,
	AuthorityPattern,
	UserTypesEnum,
} from 'ufinet-web-functions'
import { PrivateRoutes } from './protected/PrivateRoutes'

const extractTokenFromUrl = (): ITokenData | null => {
	const urlParams = new URLSearchParams(window.location.search)
	const accessToken = urlParams.get('token')
	const refreshToken = urlParams.get('refreshToken')
	if (accessToken) {
		return { accessToken, refreshToken: refreshToken || '' }
	}
	return null
}

const cleanTokenFromUrl = () => {
	const url = new URL(window.location.href)
	url.searchParams.delete('token')
	url.searchParams.delete('refreshToken')
	window.history.replaceState({}, '', url.toString())
}

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
	try {
		const base64Url = token.split('.')[1]
		const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split('')
				.map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
				.join('')
		)
		return JSON.parse(jsonPayload)
	} catch {
		return null
	}
}

const buildUserDataFromJwt = (accessToken: string): IAuthUser | null => {
	const payload = decodeJwtPayload(accessToken)
	if (!payload) return null

	const authorities: AuthorityPattern[] =
		typeof payload.roles === 'string' ? (payload.roles.split(',') as AuthorityPattern[]) : []

	return {
		valid: true,
		expired: false,
		username: (payload.sub as string) || '',
		authorities,
		user: {
			fullName: (payload.fullname as string) || '',
			type: ((payload.usertype as string) || '') as UserTypesEnum,
		},
	}
}

const fetchAuthorities = async (accessToken: string): Promise<AuthorityPattern[] | null> => {
	const baseUrl = process.env.REACT_APP_AUTHORITIES_BASE_URL
	const apiKey = process.env.REACT_APP_AUTHORITIES_API_KEY
	if (!baseUrl || !apiKey) {
		console.warn('Authorities env vars not configured, falling back to JWT roles')
		return []
	}
	try {
		const response = await fetch(`${baseUrl}portalbackend/api/authorities`, {
			method: 'GET',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				apiKey: apiKey,
			},
		})
		if (response.status === 401) {
			redirectToPortalLogin()
			return null
		}
		if (!response.ok) return []
		const data = await response.json()
		if (data?.authorities && Array.isArray(data.authorities) && data.authorities.length > 0) {
			return data.authorities as AuthorityPattern[]
		}
		return []
	} catch {
		return []
	}
}

const buildUserDataWithAuthorities = async (accessToken: string): Promise<IAuthUser | null> => {
	const userData = buildUserDataFromJwt(accessToken)
	if (!userData) return null
	const fetchedAuthorities = await fetchAuthorities(accessToken)
	if (fetchedAuthorities === null) return null
	if (fetchedAuthorities.length > 0) {
		userData.authorities = fetchedAuthorities
	}
	return userData
}

const refreshAccessToken = async (): Promise<string | null> => {
	const baseUrl = process.env.REACT_APP_AUTHORITIES_BASE_URL
	const apiKey = process.env.REACT_APP_AUTHORITIES_API_KEY
	if (!baseUrl || !apiKey) return null

	const stored = localStorage.getItem('ufinet-auth')
	if (!stored) return null
	const auth = JSON.parse(stored)
	const tokenObj = typeof auth.token === 'object' ? auth.token : JSON.parse(auth.token)
	const { accessToken, refreshToken } = tokenObj
	if (!accessToken || !refreshToken) return null

	const response = await fetch(`${baseUrl}portalbackend/api/auth/token/refresh`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${accessToken}`,
			ApiKey: apiKey,
		},
		body: JSON.stringify({ refreshToken }),
	})

	if (!response.ok) return null
	const data = await response.json()
	return data.accessToken ?? null
}

const redirectToPortalLogin = () => {
	const portalLoginUrl = process.env.REACT_APP_URL_PORTAL_LOGIN
	if (!portalLoginUrl) {
		console.error('REACT_APP_URL_PORTAL_LOGIN is not configured')
		return
	}
	const currentUrl = window.location.href
	window.location.href = `${portalLoginUrl}?fromLegacyUrl=${encodeURIComponent(currentUrl)}`
}

const AppRoutes: FC = () => {
	const intl = useIntl()
	const { status, setStatus, token, setToken, clear } = useContext(AuthContext)
	const hasInitialized = useRef(false)

	// On mount: extract token from URL or use existing, fetch authorities, and log in
	useEffect(() => {
		if (hasInitialized.current) return
		hasInitialized.current = true

		const initAuth = async () => {
			authService.setUpBroadcastChannel({ onLogoutReceived: clear })

			const urlToken = extractTokenFromUrl()
			if (urlToken) {
				cleanTokenFromUrl()
				setToken(urlToken)
			}

			const tokenToUse = urlToken || token
			if (!tokenToUse?.accessToken) {
				redirectToPortalLogin()
				return
			}

			const userData = await buildUserDataWithAuthorities(tokenToUse.accessToken)
			if (!userData) {
				redirectToPortalLogin()
				return
			}

			setStatus(AuthStatus.LOGGED_IN, userData)
		}

		initAuth()
	}, [])

	// Handle logout triggered by FetchAPI 401/403 or BroadcastChannel
	useEffect(() => {
		if (hasInitialized.current && status === AuthStatus.LOGGED_OUT) {
			redirectToPortalLogin()
		}
	}, [status])

	// Periodically refresh token
	const refreshToken = useCallback(() => {
		if (status === AuthStatus.LOGGED_IN) {
			refreshAccessToken()
				.then((newAccessToken) => {
					if (newAccessToken) {
						setToken({ accessToken: newAccessToken, refreshToken: token?.refreshToken || '' })
					} else {
						throw new Error('Refresh failed')
					}
				})
				.catch(() => {
					toast.dismiss()
					toast.error(intl.formatMessage({ id: 'AUTH.SESSION.EXPIRED' }))
					redirectToPortalLogin()
				})
		}
	}, [status, setToken, intl, token?.refreshToken])

	useEffect(() => {
		const refreshInterval = setInterval(refreshToken, 15 * 60 * 1000)

		return () => {
			clearInterval(refreshInterval)
		}
	}, [refreshToken])

	return (
		<BrowserRouter>
			<Routes>
				<Route element={<App />}>
					{status === AuthStatus.LOGGED_IN ? (
						<>
							<Route path="/*" element={<PrivateRoutes />} />
						</>
					) : (
						<>
							<Route path="/*" element={<VerificationPage />} />
						</>
					)}
				</Route>
			</Routes>
		</BrowserRouter>
	)
}

export { AppRoutes }
